import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { franchiseApi } from '@/api'
import { selectProfile } from '@/store/profile.reducer'

interface FranchiseOption {
  id: number
  name: string
}

interface ProfileFranchise {
  id?: number | string | null
}

const getProfileFranchiseIds = (profile: any): number[] => {
  const directIds = Array.isArray(profile?.franchise_ids)
    ? profile.franchise_ids
    : []
  const relationIds = Array.isArray(profile?.franchises)
    ? profile.franchises.map((franchise: ProfileFranchise) => franchise?.id)
    : []

  return Array.from(
    new Set([...directIds, ...relationIds].map(Number).filter(Boolean)),
  )
}

export const useFranchises = () => {
  const profile = useSelector(selectProfile)
  const [enabled, setEnabled] = useState(false)
  const [items, setItems] = useState<FranchiseOption[]>([])
  const [autoSelect, setAutoSelect] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    franchiseApi
      .getOptions()
      .then((response) => {
        if (!mounted) return
        const payload = response?.data?.data ?? {}
        setEnabled(Boolean(payload.enabled))
        setItems(Array.isArray(payload.items) ? payload.items : [])
        setAutoSelect(
          payload.auto_select_id == null ? null : Number(payload.auto_select_id),
        )
      })
      .catch(() => {
        if (!mounted) return
        setEnabled(false)
        setItems([])
        setAutoSelect(null)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [])

  const profileFranchiseIds = useMemo(
    () => getProfileFranchiseIds(profile),
    [profile],
  )

  const availableItems = useMemo(() => {
    if (profile?.role !== 'franchise-manager' || !profileFranchiseIds.length) {
      return items
    }

    return items.filter((item) => profileFranchiseIds.includes(Number(item.id)))
  }, [items, profile?.role, profileFranchiseIds])

  const availableAutoSelect = useMemo(() => {
    if (!availableItems.length) {
      return null
    }

    if (profile?.role === 'franchise-manager' && availableItems.length === 1) {
      return Number(availableItems[0].id)
    }

    return autoSelect != null &&
      availableItems.some((item) => Number(item.id) === Number(autoSelect))
      ? autoSelect
      : null
  }, [autoSelect, availableItems, profile?.role])

  const options = useMemo(
    () => availableItems.map((item) => ({ label: item.name, value: item.id })),
    [availableItems],
  )

  return {
    enabled,
    items: availableItems,
    options,
    autoSelect: availableAutoSelect,
    loading,
  }
}

export default useFranchises
